// FE-SCRM-050 — Stakeholder section on the Prospect detail page.
//
// The canonical stakeholder map is opportunity-scoped (FE-SCRM-013),
// because decision-roles live per-opportunity. The prospect-level
// summary aggregates roles across every opportunity at this prospect:
// fetches the opportunity list, then fetches each opportunity's
// stakeholder map in parallel, then flattens into a unique-contact
// list with one chip per opportunity showing the per-pursuit role.
//
// Read-only by design — edits route to the canonical
// OpportunityDetailPage (FE-SCRM-011) where the producer can re-rank
// stance / influence in context. The "Edit on opportunity" link on
// each chip is the affordance.

import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { Card } from "@/design-system";
import {
  DashboardEmptyState,
  DashboardLoadingState,
} from "@/features/dashboard";

import {
  DECISION_ROLE_LABEL,
  STAKEHOLDER_MAP_KEY,
  type StakeholderMapResponse,
  type StakeholderRow,
} from "../StakeholderMap";
import * as smApi from "../StakeholderMap/api";

import { useProspectOpportunitiesQuery } from "./hooks";


export interface ProspectStakeholderSectionProps {
  prospect_id: string;
}


interface AggregatedRow {
  contact_id: string;
  first_name: string;
  last_name: string;
  title: string | null;
  email: string | null;
  per_opportunity: Array<{
    opportunity_id: string;
    opportunity_name: string;
    role: StakeholderRow;
  }>;
}


function aggregate(
  opps: Array<{ opportunity_id: string; name: string }>,
  maps: Array<StakeholderMapResponse | undefined>,
): AggregatedRow[] {
  const byContact = new Map<string, AggregatedRow>();

  for (let i = 0; i < opps.length; i += 1) {
    const opp = opps[i];
    const map = maps[i];
    if (!map) continue;
    const rows = Object.values(map.roles ?? {}).flat();
    for (const row of rows) {
      const existing = byContact.get(row.contact_id);
      const next: AggregatedRow["per_opportunity"][number] = {
        opportunity_id: opp.opportunity_id,
        opportunity_name: opp.name,
        role: row,
      };
      if (existing) {
        existing.per_opportunity.push(next);
        continue;
      }
      byContact.set(row.contact_id, {
        contact_id: row.contact_id,
        first_name: row.first_name,
        last_name: row.last_name,
        title: row.title,
        email: row.email,
        per_opportunity: [next],
      });
    }
  }

  // Sort contacts by how many pursuits they touch (desc), then by
  // last name. A contact across three deals shows up before one on a
  // single deal — the producer's attention should flow that way.
  return Array.from(byContact.values()).sort((a, b) => {
    if (b.per_opportunity.length !== a.per_opportunity.length) {
      return b.per_opportunity.length - a.per_opportunity.length;
    }
    return a.last_name.localeCompare(b.last_name);
  });
}


export function ProspectStakeholderSection({
  prospect_id,
}: ProspectStakeholderSectionProps) {
  const oppsQuery = useProspectOpportunitiesQuery(prospect_id);
  const opps = oppsQuery.data?.rows ?? [];

  const mapsQueries = useQueries({
    queries: opps.map((o) => ({
      queryKey: [STAKEHOLDER_MAP_KEY, o.opportunity_id],
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        smApi.fetchStakeholderMap(o.opportunity_id, { signal }),
      enabled: Boolean(o.opportunity_id),
    })),
  });

  const isLoading =
    oppsQuery.isLoading
    || mapsQueries.some((q) => q.isLoading && !q.data);

  const aggregated = useMemo(
    () =>
      aggregate(
        opps,
        mapsQueries.map((q) => q.data),
      ),
    [opps, mapsQueries],
  );

  if (oppsQuery.isLoading) {
    return (
      <Card data-testid="prospect-stakeholder-section">
        <DashboardLoadingState variant="card" />
      </Card>
    );
  }

  if (opps.length === 0) {
    return (
      <Card data-testid="prospect-stakeholder-section">
        <SectionHeader />
        <DashboardEmptyState
          title="No opportunities yet"
          description={
            "Add an opportunity at this prospect to start mapping "
            + "decision-makers."
          }
        />
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card data-testid="prospect-stakeholder-section">
        <SectionHeader />
        <DashboardLoadingState variant="card" />
      </Card>
    );
  }

  if (aggregated.length === 0) {
    return (
      <Card data-testid="prospect-stakeholder-section">
        <SectionHeader />
        <DashboardEmptyState
          title="No stakeholders mapped yet"
          description={
            "Map the people involved in each pursuit from the "
            + "Opportunity detail page to populate this view."
          }
        />
      </Card>
    );
  }

  return (
    <Card
      data-testid="prospect-stakeholder-section"
      className="flex flex-col gap-3"
    >
      <SectionHeader />
      <ul
        data-testid="prospect-stakeholder-list"
        className="flex flex-col divide-y divide-border-subtle"
      >
        {aggregated.map((row) => (
          <li
            key={row.contact_id}
            data-testid={`prospect-stakeholder-${row.contact_id}`}
            className="flex flex-col gap-1.5 py-3"
          >
            <div className="flex items-baseline justify-between gap-3">
              <div className="flex flex-col">
                <span className="text-sm font-medium text-text-primary">
                  {row.first_name} {row.last_name}
                </span>
                {(row.title || row.email) && (
                  <span className="text-xs text-text-tertiary">
                    {[row.title, row.email].filter(Boolean).join(" · ")}
                  </span>
                )}
              </div>
              <span className="text-[11px] uppercase tracking-wide text-text-tertiary">
                {row.per_opportunity.length}{" "}
                {row.per_opportunity.length === 1
                  ? "opportunity"
                  : "opportunities"}
              </span>
            </div>
            <ul className="flex flex-wrap gap-1.5">
              {row.per_opportunity.map((p) => (
                <li
                  key={`${row.contact_id}:${p.opportunity_id}`}
                  className="rounded-full border border-border-subtle px-2 py-0.5 text-[11px] text-text-secondary"
                >
                  <Link
                    to={`/dashboard/sales-crm/opportunities/${encodeURIComponent(
                      p.opportunity_id,
                    )}`}
                    className="hover:underline"
                  >
                    {p.opportunity_name} ·{" "}
                    {DECISION_ROLE_LABEL[
                      p.role.decision_role as keyof typeof DECISION_ROLE_LABEL
                    ] ?? p.role.decision_role}
                  </Link>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </Card>
  );
}


function SectionHeader() {
  return (
    <header className="flex flex-col gap-0.5">
      <h3 className="text-md font-semibold text-text-primary">
        Stakeholders
      </h3>
      <p className="text-xs text-text-secondary">
        People mapped across this prospect's opportunities. Edit roles
        on the Opportunity detail page.
      </p>
    </header>
  );
}
